import React, { useState } from "react";
import {
  FaCloud,
  FaCogs,
  FaRobot,
  FaGithub,
  FaLinkedin,
  FaAws,
  FaEnvelope,
  FaBars,
  FaTimes
} from "react-icons/fa";

function Project({
  icon,
  title,
  problem,
  solution,
  highlight,
  techStack,
  github,
  demo,
  pdfLink
}) {
  return (
    <div className="bg-gray-900 border border-gray-800 p-8 md:p-12 rounded-xl mb-16 text-center max-w-4xl mx-auto 
                    shadow-lg transition duration-300 
                    hover:scale-105 hover:border-red-500 hover:shadow-green-500/20 
                    active:scale-110 cursor-pointer">

      {icon}

      <h3 className="text-2xl md:text-3xl font-bold mb-6 
                     bg-gradient-to-r from-yellow-400 to-orange-400 
                     text-transparent bg-clip-text">
        {title}
      </h3>

      <p className="text-red-400 font-bold text-xs uppercase tracking-wider text-left mb-1">Problem</p>
      <p className="text-gray-300 mb-8 text-left leading-7">{problem}</p>

      <p className="text-green-400 font-bold text-xs uppercase tracking-wider text-left mb-1">Solution</p>
      <p className="text-gray-300 mb-8 text-left leading-7">{solution}</p>

      <p className="text-yellow-400 font-semibold mb-6">
        {highlight}
      </p>

      {/* TECH STACK */}
      {techStack && (
        <div className="flex flex-wrap justify-center gap-2 mb-6">
          {techStack.map((tech, index) => (
            <span
              key={index}
              className="bg-gray-800 text-gray-300 px-3 py-1 rounded-full text-sm"
            >
              {tech}
            </span>
          ))}
        </div>
      )}

      {/* BUTTONS */}
      <div className="flex flex-wrap justify-center gap-4">

        <a href={demo} target="_blank" rel="noopener noreferrer">
          <button className="bg-transparent border border-blue-500 px-6 py-3 rounded-full hover:bg-blue-600 hover:border-blue-600 hover:scale-105 transition">
            🎥 Demo Video
          </button>
        </a>

        <a href={github} target="_blank" rel="noopener noreferrer">
          <button className="bg-transparent border border-red-500 px-6 py-3 rounded-full hover:bg-red-500 hover:border-red-500 hover:scale-105 transition">
            GitHub
          </button>
        </a>

        <a href={pdfLink} target="_blank" rel="noopener noreferrer">
          <button className="bg-transparent border border-teal-400 px-6 py-3 rounded-full hover:bg-gradient-to-r hover:from-green-400 hover:to-teal-500 hover:border-teal-400 hover:scale-105 transition">
            Project Details
          </button>
        </a>

      </div>
    </div>
  );
}

function App() {
  const [menuOpen, setMenuOpen] = useState(false);

  const navLinks = [
    { href: "#about", label: "About" },
    { href: "#experience", label: "Experience" },
    { href: "#cert", label: "Certification" },
    { href: "#skills", label: "Skills" },
    { href: "#projects", label: "Projects" },
    { href: "#resume", label: "Resume" },
    { href: "#contact", label: "Contact" },
  ];

  return (
    <div className="bg-black text-white scroll-smooth">

      {/* NAVBAR */}
      <nav className="fixed top-0 w-full bg-black/90 backdrop-blur-md border-b border-gray-800 z-50">
        <div className="max-w-7xl mx-auto flex justify-between items-center px-6 md:px-12 py-5">

          <h1 className="text-3xl font-bold">
            Siva<span className="text-red-500">.</span>
          </h1>

          {/* Desktop links */}
          <div className="hidden md:flex gap-8 text-gray-300">
            {navLinks.map((link) => (
              <a key={link.href} href={link.href} className="hover:text-red-400 transition">
                {link.label}
              </a>
            ))}
          </div>

          {/* Mobile menu toggle */}
          <button
            className="md:hidden text-2xl text-gray-200"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
          >
            {menuOpen ? <FaTimes /> : <FaBars />}
          </button>
        </div>

        {/* Mobile menu panel */}
        {menuOpen && (
          <div className="md:hidden bg-black border-t border-gray-800 px-6 py-4 flex flex-col gap-4">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="text-gray-300 hover:text-red-400 transition"
                onClick={() => setMenuOpen(false)}
              >
                {link.label}
              </a>
            ))}
          </div>
        )}
      </nav>

      {/* HERO */}
      <section className="min-h-screen flex items-center bg-gradient-to-b from-black via-gray-900 to-black pt-24 md:pt-0">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-2 gap-12 items-center">

          <div>
            <p className="text-red-400 font-semibold mb-3">👋 Hello, I'm</p>

            <h1 className="text-4xl sm:text-5xl md:text-7xl font-extrabold leading-tight">
              M D
              <span className="text-red-500 block">Siva Ram</span>
            </h1>

            <p className="mt-8 text-lg md:text-xl text-gray-300 leading-9">
              AI Cloud Engineer building{" "}
              <span className="text-white font-semibold">Agentic AI</span> and{" "}
              <span className="text-white font-semibold">RAG systems</span>, and
              deploying them as production-ready{" "}
              <span className="text-white font-semibold">AWS Cloud</span> applications.
            </p>

            <div className="flex flex-wrap gap-3 mt-8">
              {["LangChain", "LangGraph", "RAG", "FastAPI", "AWS", "Groq"].map((tag) => (
                <span key={tag} className="bg-red-500/20 px-4 py-2 rounded-full text-sm md:text-base">
                  {tag}
                </span>
              ))}
            </div>

            <div className="flex flex-wrap gap-4 md:gap-5 mt-10">
              <a href="#projects">
                <button className="bg-transparent border border-red-500 px-6 md:px-7 py-3 rounded-full hover:bg-red-500 hover:border-red-500 transition">
                  View Projects
                </button>
              </a>

              <a href="/DSR_83.pdf" target="_blank" rel="noopener noreferrer">
                <button className="bg-transparent border border-red-500 px-6 md:px-7 py-3 rounded-full hover:bg-red-500 hover:border-red-500 transition">
                  Resume
                </button>
              </a>

              <a href="https://github.com/dimbu04" target="_blank" rel="noopener noreferrer">
                <button className="bg-transparent border border-red-500 px-6 md:px-7 py-3 rounded-full hover:bg-red-500 hover:border-red-500 transition">
                  GitHub
                </button>
              </a>
            </div>
          </div>

          <div className="flex justify-center">
            <img
              src="/profile.png"
              alt="M D Siva Ram"
              loading="eager"
              className="w-56 sm:w-72 md:w-96 rounded-full border-4 border-red-500 shadow-2xl"
            />
          </div>

        </div>
      </section>

      {/* ABOUT */}
      <section id="about" className="max-w-7xl mx-auto px-6 md:px-12 py-24">
        <h2 className="text-4xl font-bold mb-10">About Me</h2>

        <p className="text-gray-300 leading-9 text-lg">
          I'm <span className="text-red-400 font-semibold">M D Siva Ram</span>, an{" "}
          <span className="text-white font-semibold">AI Cloud Engineer</span> who builds
          intelligent systems and ships them as real, running applications on{" "}
          <span className="text-red-400">AWS</span>, not just prototypes. My work combines{" "}
          <span className="text-red-400">Large Language Models</span>,{" "}
          <span className="text-red-400">Retrieval-Augmented Generation (RAG)</span>,{" "}
          <span className="text-red-400">Agentic AI</span>, and{" "}
          <span className="text-red-400">AWS Cloud</span> (S3, Lambda, API Gateway, CloudFront,
          DynamoDB, EC2, VPC) to design and deploy scalable, production-ready applications end
          to end, from architecture to live deployment.
          <br /><br />
          Currently working as an <span className="text-white font-semibold">AI Visibility &amp; AEO Intern</span>{" "}
          at <span className="text-red-400">WinTraction (Florida, USA)</span>, remotely, where I
          optimize AI discoverability across platforms including ChatGPT, Gemini, Grok and
          Perplexity while contributing to AI-powered content systems.
        </p>
      </section>

      {/* EXPERIENCE */}
      <section id="experience" className="bg-[#0d0d0d] py-24 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">

          <h2 className="text-4xl font-bold mb-12 text-center">Professional Experience</h2>

          <div className="bg-gray-900 border border-gray-800 rounded-2xl p-8 hover:border-red-500 hover:-translate-y-2 hover:shadow-lg hover:shadow-red-500/10 transition">

            <div className="flex flex-col md:flex-row md:justify-between md:items-center">
              <div>
                <h3 className="text-2xl font-bold">AI Visibility &amp; AEO Intern</h3>
                <p className="text-red-400 mt-2">WinTraction, Florida, USA, Remote</p>
              </div>
              <span className="text-gray-400 mt-4 md:mt-0">Jun 2026 - Present</span>
            </div>

            <ul className="mt-8 space-y-4 text-gray-300 list-disc pl-6">
              <li>Optimized AI visibility across ChatGPT, Gemini, Grok and Perplexity.</li>
              <li>Created AEO-focused blogs, landing pages, comparison pages and structured FAQ content.</li>
              <li>Improved brand discoverability through AI citation optimization and content strategy.</li>
              <li>Conducted AI platform audits and competitive research to improve answer engine rankings.</li>
            </ul>

          </div>
        </div>
      </section>

      {/* CERT */}
      <section id="cert" className="px-6 md:px-12 py-24 text-center">
        <h2 className="text-4xl font-bold mb-10">Certification</h2>
        <p className="text-gray-200 text-lg mb-12">
          Industry-recognized <span className="text-red-400 font-semibold">cloud certification</span> validating
          AWS foundational knowledge.
        </p>
        <div className="flex justify-center">
          <div className="bg-gray-900 border border-gray-800 p-10 rounded-xl w-full max-w-sm hover:scale-105 hover:border-red-500 transition">
            <img
              src="/aws2.png"
              alt="AWS Certified Cloud Practitioner Badge"
              className="w-52 mx-auto"
            />
            <h3 className="mt-6 text-xl font-bold text-yellow-400">
              AWS Certified Cloud Practitioner
            </h3>
            <a
              href="https://www.credly.com/badges/82cf0676-29b5-4eea-8749-85c72bb98250/public_url"
              target="_blank"
              rel="noopener noreferrer"
            >
              <button className="bg-transparent border border-red-500 px-6 py-2 mt-6 rounded-full hover:bg-red-500 hover:border-red-500 hover:scale-105 transition">
                Verify Credential
              </button>
            </a>
          </div>
        </div>
      </section>

      {/* SKILLS */}
      <section id="skills" className="py-24 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">

          <h2 className="text-4xl font-bold text-center mb-14">Technical Skills</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">

            <div className="bg-gray-900 border border-gray-800 p-8 rounded-xl hover:-translate-y-2 hover:border-red-500 transition">
              <h3 className="text-xl font-bold text-red-400 mb-5">Languages</h3>
              <p className="text-gray-300">Python<br />JavaScript</p>
            </div>

            <div className="bg-gray-900 border border-gray-800 p-8 rounded-xl hover:-translate-y-2 hover:border-red-500 transition">
  <h3 className="text-xl font-bold text-red-400 mb-5">LLMs &amp; AI Systems</h3>
  <p className="text-gray-300">Groq API<br />Hugging Face (Transformers, Inference API)<br />LLM API Integration<br />Prompt Engineering<br />LLM-based Chatbots<br />Multi-Agent Systems<br />RAG Architecture<br />ChromaDB</p>
</div>

            <div className="bg-gray-900 border border-gray-800 p-8 rounded-xl hover:-translate-y-2 hover:border-red-500 transition">
              <h3 className="text-xl font-bold text-red-400 mb-5">Tools &amp; Frameworks</h3>
              <p className="text-gray-300">LangChain<br />LangGraph<br />Playwright</p>
            </div>

            <div className="bg-gray-900 border border-gray-800 p-8 rounded-xl hover:-translate-y-2 hover:border-red-500 transition">
              <h3 className="text-xl font-bold text-red-400 mb-5">Cloud &amp; Databases</h3>
              <p className="text-gray-300">AWS (EC2, S3, Lambda, API Gateway, CloudFront, VPC, DynamoDB, IAM, CloudWatch, AWS CLI)<br />MongoDB</p>
            </div>

            <div className="bg-gray-900 border border-gray-800 p-8 rounded-xl hover:-translate-y-2 hover:border-red-500 transition">
              <h3 className="text-xl font-bold text-red-400 mb-5">Development</h3>
              <p className="text-gray-300">HTML<br />CSS<br />React.js<br />Node.js<br />FastAPI<br />REST APIs</p>
            </div>

            <div className="bg-gray-900 border border-gray-800 p-8 rounded-xl hover:-translate-y-2 hover:border-red-500 transition">
              <h3 className="text-xl font-bold text-red-400 mb-5">Version Control &amp; Tools</h3>
              <p className="text-gray-300">Git<br />GitHub<br />VS Code</p>
            </div>

          </div>
        </div>
      </section>

      {/* PROJECTS */}
      <section id="projects" className="px-6 md:px-12 py-24">
        <h2 className="text-4xl font-bold mb-4 text-center">Featured Projects</h2>

        <p className="text-gray-200 text-lg mt-4 mb-12 max-w-3xl mx-auto text-center">
          AI and Cloud projects <span className="text-red-400 font-semibold">built and deployed
          end-to-end on AWS</span>, from architecture design through live, running systems.
        </p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16 max-w-4xl mx-auto">
          <div className="bg-gray-900 p-6 rounded-xl text-center hover:-translate-y-2 hover:border hover:border-red-500 transition">
            <h3 className="text-3xl font-bold text-red-400">4+</h3>
            <p className="text-gray-400">Projects</p>
          </div>
          <div className="bg-gray-900 p-6 rounded-xl text-center hover:-translate-y-2 hover:border hover:border-red-500 transition">
            <h3 className="text-3xl font-bold text-red-400">1</h3>
            <p className="text-gray-400">AWS Certification</p>
          </div>
          <div className="bg-gray-900 p-6 rounded-xl text-center hover:-translate-y-2 hover:border hover:border-red-500 transition">
            <h3 className="text-3xl font-bold text-red-400">5+</h3>
            <p className="text-gray-400">AI Technologies</p>
          </div>
          <div className="bg-gray-900 p-6 rounded-xl text-center hover:-translate-y-2 hover:border hover:border-red-500 transition">
            <h3 className="text-3xl font-bold text-red-400">1</h3>
            <p className="text-gray-400">Internship</p>
          </div>
        </div>

        <Project
          icon={<FaCogs className="text-green-400 text-5xl mx-auto mb-4" />}
          title="Multi-Agent AI Automation System"
          problem="Content teams at early-stage startups spend hours every week manually researching topics, writing posts, sourcing or generating images, and publishing across platforms. This is a repetitive, low-leverage workflow that doesn't scale as content volume grows. Stitching together separate tools for research, writing, and publishing also introduces inconsistency: tone drifts, formatting breaks, and nothing runs on a schedule without a human pushing every step by hand. Most automation scripts solve one piece of this (just scraping, or just generation) but don't coordinate multiple specialized steps into a single reliable pipeline."
          solution="Designed and deployed a modular multi-agent pipeline using LangChain and LangGraph, where each stage of the workflow (scraping/research, content generation, image creation, and publishing) is handled by its own dedicated agent with a clearly scoped responsibility, coordinated through a shared graph-based state machine instead of one monolithic script. Used the Groq API for low-latency LLM inference so the pipeline runs fast enough for near real-time content turnaround. Automated the final publishing step end-to-end with Playwright, driving real browser actions so content goes live without manual copy-pasting. Deployed the entire system on AWS EC2 behind a Streamlit interface, so it isn't just a local script, it's a persistently running, remotely accessible application that a non-technical user could trigger and monitor. This is the project I'd point to as proof I can take an AI system from architecture through to a deployed, working product on AWS, not just a notebook demo."
          highlight="A production-style, end-to-end AI automation pipeline, from LLM-driven content generation to live publishing, deployed and running on AWS."
          techStack={["LangChain", "LangGraph", "Groq API", "Playwright", "AWS EC2", "Streamlit"]}
          github="https://github.com/dimbu04/multi-agent-social-ai"
          demo="https://www.linkedin.com/posts/m-d-siva-ram-840582345_ai-langchain-langgraph-activity-7455673120272670722-nw97?utm_source=share&utm_medium=member_desktop&rcm=ACoAAFZnZjABp7votSvH5yG5g0sGoi1Wcl3deEg"
          pdfLink="https://drive.google.com/file/d/1WvDVQKd-VeVTAUQWTHLPyKvAlFuJIKQd/view?usp=sharing"
        />

        <Project
          icon={<FaRobot className="text-purple-400 text-5xl mx-auto mb-4" />}
          title="Serverless AI Chatbot"
          problem="Building an AI chat application the traditional way means running and scaling a backend server around the clock, even though usage is bursty. Most of the time it's idle, then it needs to handle a spike of concurrent users with low latency. Provisioning for peak load wastes money at idle, and provisioning for average load causes slow or dropped responses at peak. Managing that scaling manually adds ongoing operational work that a small team or solo builder shouldn't need to carry."
          solution="Built a fully serverless chatbot where AWS Lambda handles each incoming request as an independent, auto-scaling function instead of a persistent server, so compute cost is near-zero at idle and scales automatically under load. API Gateway routes and manages incoming requests to the Lambda functions, and the frontend is hosted on Amazon S3, keeping the entire stack serverless end to end. Integrated the HuggingFace API for response generation, so the system offloads the heavy inference work rather than hosting a model directly on constrained compute."
          highlight="Production-ready AI chatbot powered by a fully serverless AWS architecture with near-zero idle cost."
          techStack={["AWS Lambda", "API Gateway", "AWS S3", "HuggingFace"]}
          github="https://github.com/dimbu04/aws-ai-chatbot-serverless"
          demo="https://www.linkedin.com/posts/m-d-siva-ram-840582345_aws-cloudcomputing-serverless-activity-7436283891843416064--0tT?utm_source=share&utm_medium=member_desktop&rcm=ACoAAFZnZjABp7votSvH5yG5g0sGoi1Wcl3deEg"
          pdfLink="https://drive.google.com/file/d/12GDB8SyjtvVu8m33ZxyLPExqJAtD05Jq/view?usp=sharing"
        />

        <Project
          icon={<FaCloud className="text-blue-400 text-5xl mx-auto mb-4" />}
          title="Secure File Upload System"
          problem="Handling file uploads securely at scale means solving several problems at once: keeping large files off the application server so uploads don't bottleneck it, controlling who can access what after upload, processing files automatically once they land, and keeping metadata queryable without scanning raw storage. Doing this with a single monolithic backend creates a single point of failure and a scaling bottleneck for what should be a highly parallel operation."
          solution="Implemented S3 presigned URLs so clients upload files directly to S3 without ever routing large payloads through a backend server, removing that bottleneck entirely. Used Lambda functions triggered on S3 upload events to process files automatically and asynchronously, with no polling and no manual triggers. Stored file metadata in DynamoDB for fast, queryable lookups instead of scanning S3 directly. Delivered processed files through CloudFront for fast, secure access, and deployed the orchestration layer on EC2 inside a VPC to keep internal traffic isolated from the public internet."
          highlight="A production-ready, event-driven file upload architecture built for security and horizontal scale."
          techStack={["AWS EC2", "AWS S3", "Lambda", "DynamoDB", "CloudFront", "VPC"]}
          github="https://github.com/dimbu04/aws-secure-file-processing-system"
          demo="https://www.linkedin.com/posts/m-d-siva-ram-840582345_aws-cloudarchitecture-cloudcomputing-activity-7441873594625896449-QaRD?utm_source=share&utm_medium=member_desktop&rcm=ACoAAFZnZjABp7votSvH5yG5g0sGoi1Wcl3deEg"
          pdfLink="https://drive.google.com/file/d/1lEMUH95wdPv-UYQ1XkshT2huxRXFjUe_/view?usp=sharing"
        />

        <Project
          icon={<FaAws className="text-orange-400 text-6xl mx-auto mb-4" />}
          title="Static Website Hosting on AWS"
          problem="Traditional web hosting requires provisioning and managing servers, handling scaling manually during traffic spikes, and building security in as a separate concern. All of this adds operational overhead for something as simple as serving a static site. Static assets don't need backend compute at all, yet most beginner deployments still reach for a full server stack, which increases both cost and attack surface without adding real value."
          solution="Designed a fully serverless architecture using Amazon S3 to host static website assets with high durability and zero server management. Integrated CloudFront as a CDN in front of S3 to cache and deliver content from edge locations globally, cutting latency for visitors regardless of region. Configured AWS WAF in front of the distribution to filter common web threats (SQL injection patterns, bad bots, rate-based rules) before they ever reach the origin. The result is a hosting setup that scales automatically with traffic, has no servers to patch or manage, and costs a fraction of a traditional VM-based setup."
          highlight="Scalable, secure and cost-efficient serverless website hosting, running with zero managed servers."
          techStack={["AWS S3", "CloudFront", "AWS WAF"]}
          github="https://github.com/dimbu04/aws-static-portfolio-hosting"
          demo="https://www.linkedin.com/posts/m-d-siva-ram-840582345_aws-cloudcomputing-devops-activity-7456767390098509825-CmG1?utm_source=share&utm_medium=member_desktop&rcm=ACoAAFZnZjABp7votSvH5yG5g0sGoi1Wcl3deEg"
          pdfLink="https://drive.google.com/file/d/193oC7JI_wVSIEV6ej8FlNkAQm_mMkAB3/view?usp=sharing"
        />
      </section>

      {/* RESUME */}
      <section id="resume" className="text-center py-24 px-6">
        <h2 className="text-4xl font-bold">Resume</h2>

        <p className="text-gray-200 text-lg mt-5 mb-8 max-w-2xl mx-auto">
          Download my latest resume showcasing <span className="text-red-400 font-semibold">AI
          Engineering, AWS Cloud, Agentic AI, RAG Systems</span>, and Full-Stack Development projects.
        </p>

        <a href="/DSR_83.pdf" target="_blank" rel="noopener noreferrer">
          <button className="bg-transparent border border-red-500 px-8 py-3 rounded-full hover:bg-red-500 hover:border-red-500 hover:scale-110 hover:shadow-lg hover:shadow-red-500/30 transition">
            Download Resume
          </button>
        </a>
      </section>

      {/* CONNECT */}
      <section className="py-20 text-center px-6">
        <h2 className="text-4xl font-bold mb-6">Let's Connect</h2>

        <p className="text-gray-200 text-lg mb-8 max-w-2xl mx-auto">
          Open to <span className="text-red-400 font-semibold">remote AI Engineering, Cloud,
          and GenAI internships</span> with early-stage, funded startups.
        </p>

        <a
          href="https://mail.google.com/mail/?view=cm&fs=1&to=manupatisivaram@gmail.com"
          target="_blank"
          rel="noopener noreferrer"
        >
          <button className="bg-transparent border border-red-500 px-8 py-3 rounded-full hover:bg-red-500 hover:border-red-500 hover:scale-110 hover:shadow-lg hover:shadow-red-500/30 transition">
            Email Me
          </button>
        </a>
      </section>

      {/* FOOTER */}
      <footer id="contact" className="text-center py-20 bg-gray-900 px-6">
        <h2 className="text-2xl font-bold mb-2">M D Siva Ram</h2>
        <p className="text-gray-400">Feel free to reach out</p>

        <div className="flex justify-center gap-8 mt-6">
          <a
            href="https://github.com/dimbu04"
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-300 hover:text-red-400 hover:scale-125 transition transform inline-block"
          >
            <FaGithub size={30} />
          </a>

          <a
            href="https://www.linkedin.com/in/m-d-siva-ram-840582345/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-300 hover:text-red-400 hover:scale-125 transition transform inline-block"
          >
            <FaLinkedin size={30} />
          </a>

          <a
            href="https://mail.google.com/mail/?view=cm&fs=1&to=manupatisivaram@gmail.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-300 hover:text-red-400 hover:scale-125 transition transform inline-block"
          >
            <FaEnvelope size={32} />
          </a>
        </div>

        <p className="mt-6 text-gray-500">© M D Siva Ram</p>
      </footer>

    </div>
  );
}

export default App;